import { useState, useEffect, useCallback } from 'react';
import {
  Table, Title, Badge, Text, Stack, Loader, Center, Alert, Group, UnstyledButton, Button,
  ActionIcon, Tooltip,
} from '@mantine/core';
import { IconAlertCircle, IconChevronUp, IconChevronDown, IconSelector, IconPlus, IconEdit } from '@tabler/icons-react';
import { api } from '../../apiClient';
import AddCertificationModal from './AddCertificationModal';
import EditCertificationModal from './EditCertificationModal';

/**
 * 資格管理パネル
 * certifications テーブルの一覧と各資格の問題数を表示する
 * 列ヘッダーをクリックしてソートできる
 */
function CertificationManagement() {
  const [certifications, setCertifications] = useState([]);
  const [questionCounts, setQuestionCounts] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  // ソートキーと方向を管理する（初期値: ベンダー昇順）
  const [sortKey, setSortKey] = useState('vendor');
  const [sortDir, setSortDir] = useState('asc');
  const [modalOpen, setModalOpen] = useState(false);
  const [editTarget, setEditTarget] = useState(null);

  const fetchData = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const certs = await api.get('/certifications');

      // 資格ごとの問題数を並列取得する
      const countResults = await Promise.all(
        certs.map((c) =>
          api.get('/questions/count', { certificationId: c.id })
            .then((data) => ({ id: c.id, count: data.count ?? 0 }))
            .catch(() => ({ id: c.id, count: 0 }))
        )
      );

      const counts = countResults.reduce((acc, r) => {
        acc[r.id] = r.count;
        return acc;
      }, {});

      const sorted = [...certs].sort((a, b) =>
        a.vendor.localeCompare(b.vendor) || a.id - b.id
      );
      setCertifications(sorted);
      setQuestionCounts(counts);
    } catch (err) {
      console.error('資格管理データの取得に失敗しました:', err);
      setError('データの取得に失敗しました。');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // ベンダーキーを表示名に変換する（大文字化を防ぐため明示的に指定する）
  const vendorLabel = (vendor) => {
    if (vendor === 'aws') return 'AWS';
    if (vendor === 'microsoft') return 'Microsoft';
    if (vendor === 'google') return 'Google';
    return vendor;
  };

  // ベンダーキーに応じたバッジスタイルを返す
  // variant="light" は色の差が出にくいため、ブランドカラーを style で直接指定する
  const vendorStyle = (vendor) => {
    if (vendor === 'aws') {
      // AWS オフィシャルオレンジ
      return { backgroundColor: '#FF9900', color: '#ffffff', textTransform: 'none' };
    }
    if (vendor === 'microsoft') {
      // Microsoft オフィシャルブルー
      return { backgroundColor: '#0078D4', color: '#ffffff', textTransform: 'none' };
    }
    if (vendor === 'google') {
      // Google オフィシャルグリーン（Microsoft の青と区別するため。他画面の配色とも統一）
      return { backgroundColor: '#34A853', color: '#ffffff', textTransform: 'none' };
    }
    return { textTransform: 'none' };
  };

  // 列ヘッダークリック時にソートキー・方向を切り替える
  const handleSort = (key) => {
    if (sortKey === key) {
      setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortKey(key);
      setSortDir('asc');
    }
  };

  // ソートアイコンを返す
  const SortIcon = ({ col }) => {
    if (sortKey !== col) return <IconSelector size={14} />;
    return sortDir === 'asc' ? <IconChevronUp size={14} /> : <IconChevronDown size={14} />;
  };

  // ソート済み資格一覧を生成する
  const sorted = [...certifications].sort((a, b) => {
    let av, bv;
    if (sortKey === 'count') {
      av = questionCounts[a.id] ?? 0;
      bv = questionCounts[b.id] ?? 0;
    } else {
      av = a[sortKey] ?? '';
      bv = b[sortKey] ?? '';
    }
    if (av < bv) return sortDir === 'asc' ? -1 : 1;
    if (av > bv) return sortDir === 'asc' ? 1 : -1;
    return 0;
  });

  if (loading) {
    return (
      <Center h={200}>
        <Loader size="sm" />
      </Center>
    );
  }

  return (
    <Stack gap="lg">
      <AddCertificationModal
        opened={modalOpen}
        onClose={() => setModalOpen(false)}
        onSuccess={fetchData}
      />
      <EditCertificationModal
        opened={editTarget !== null}
        cert={editTarget}
        onClose={() => setEditTarget(null)}
        onSuccess={() => { setEditTarget(null); fetchData(); }}
      />

      <Group justify="space-between">
        <Title order={3}>資格管理</Title>
        <Group gap="sm">
          <Text size="sm" c="dimmed">合計: {certifications.length} 件</Text>
          <Button
            size="sm"
            leftSection={<IconPlus size={16} />}
            onClick={() => setModalOpen(true)}
          >
            資格を追加
          </Button>
        </Group>
      </Group>

      {error && (
        <Alert icon={<IconAlertCircle size={16} />} color="red" variant="light">
          {error}
        </Alert>
      )}

      <Table striped highlightOnHover withTableBorder>
        <Table.Thead>
          <Table.Tr>
            {/* 各列ヘッダーをクリックでソートできるようにする */}
            {[
              { key: 'id',          label: 'ID' },
              { key: 'code',        label: 'コード' },
              { key: 'name',        label: '名称' },
              { key: 'vendor',      label: 'ベンダー' },
              { key: 'url_keyword', label: 'URLキーワード' },
              { key: 'count',       label: '問題数' },
            ].map(({ key, label }) => (
              <Table.Th key={key} style={{ cursor: 'pointer', userSelect: 'none' }}>
                <UnstyledButton onClick={() => handleSort(key)}>
                  <Group gap={4} wrap="nowrap">
                    <Text size="sm" fw={600}>{label}</Text>
                    <SortIcon col={key} />
                  </Group>
                </UnstyledButton>
              </Table.Th>
            ))}
            <Table.Th />
          </Table.Tr>
        </Table.Thead>
        <Table.Tbody>
          {sorted.map((cert) => (
            <Table.Tr key={cert.id}>
              <Table.Td>
                <Text size="sm" ff="monospace">{cert.id}</Text>
              </Table.Td>
              <Table.Td>
                <Text size="sm" fw={500}>{cert.code}</Text>
              </Table.Td>
              <Table.Td>
                <Stack gap={2}>
                  <Text size="sm">{cert.name}</Text>
                  {cert.formal_name && (
                    <Text size="xs" c="dimmed">{cert.formal_name}</Text>
                  )}
                </Stack>
              </Table.Td>
              <Table.Td>
                {/* variant="filled" でないと Mantine 内部の --badge-bg が style の backgroundColor を上書きする */}
                <Badge size="sm" variant="filled" style={vendorStyle(cert.vendor)}>
                  {vendorLabel(cert.vendor)}
                </Badge>
              </Table.Td>
              <Table.Td>
                {cert.url_keyword ? (
                  <Text size="sm" ff="monospace" c="blue">{cert.url_keyword}</Text>
                ) : (
                  <Text size="sm" c="dimmed">未設定</Text>
                )}
              </Table.Td>
              <Table.Td>
                <Text size="sm" fw={500}>
                  {(questionCounts[cert.id] ?? 0).toLocaleString()}
                </Text>
              </Table.Td>
              <Table.Td>
                <Tooltip label="資格を編集" position="left">
                  <ActionIcon
                    variant="subtle"
                    size="sm"
                    onClick={() => setEditTarget(cert)}
                  >
                    <IconEdit size={14} />
                  </ActionIcon>
                </Tooltip>
              </Table.Td>
            </Table.Tr>
          ))}
        </Table.Tbody>
      </Table>
    </Stack>
  );
}

export default CertificationManagement;
